import React from 'react';

interface LoadingInitializingProps {
  message?: string;
  subMessage?: string;
}

/**
 * Professional loading component for initialization states.
 * Features animated spinners, progress dots, and subtle background patterns.
 */
export const LoadingInitializing: React.FC<LoadingInitializingProps> = ({
  message = 'Initializing Application',
  subMessage = 'Please wait while we set up your session...'
}) => {
  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center relative overflow-hidden"
      role="progressbar"
      aria-busy="true"
      aria-label={message}
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute inset-0" 
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #34A5DE 2px, transparent 2px),
                             radial-gradient(circle at 75% 75%, #3366CC 2px, transparent 2px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>
      
      {/* Main content */}
      <div className="text-center z-10 relative">
        {/* Professional spinner */}
        <div className="relative w-20 h-20 mx-auto mb-8">
          {/* Outer ring */}
          <div className="absolute inset-0 border-4 border-gray-200 rounded-full" />
          {/* Animated ring */}
          <div className="absolute inset-0 border-4 border-transparent border-t-[#34A5DE] rounded-full animate-spin" />
          {/* Inner ring */}
          <div className="absolute inset-2 border-2 border-gray-100 rounded-full" />
          <div 
            className="absolute inset-2 border-2 border-transparent border-t-[#3366CC] rounded-full animate-spin" 
            style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}
          />
          {/* Center dot */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-[#34A5DE] rounded-full animate-pulse" />
        </div>
        
        {/* Clean text */}
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-gray-700 animate-pulse">
            {message}
          </h2>
          
          {/* Progress dots */}
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-[#34A5DE] rounded-full animate-bounce" />
            <div 
              className="w-2 h-2 bg-[#3366CC] rounded-full animate-bounce" 
              style={{ animationDelay: '0.1s' }}
            />
            <div 
              className="w-2 h-2 bg-[#3762AA] rounded-full animate-bounce" 
              style={{ animationDelay: '0.2s' }}
            />
          </div>
          
          {/* Status message */}
          <p className="text-gray-500 text-sm">
            {subMessage}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoadingInitializing;
